import { Box, Divider, Popover, Tooltip } from "@mui/material";
import React, { forwardRef } from "react";

import { Typography } from "new-components/typography/Typography";

import { generateBackgroundColor } from "../utils";

import { Cell } from "./HeatMap";
import "./styles.css";

interface YData {
  xLabel: string;
  value: number | null;
  correct: number;
  total: number;
}

interface DetectorItem {
  label_prompt: string;
  detector_id: string;
}

interface ItemWithTooltipProps {
  data: YData;
  detectorData: DetectorItem;
  onClickCell: (data: any) => void;
}

function ItemWithTooltip(props: ItemWithTooltipProps) {
  const { data, detectorData, onClickCell } = props;

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const valueFixed =
    data && data.value !== null && data.value !== undefined && !Number.isNaN(data.value) ? data.value.toFixed(2) : null;

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (valueFixed === null) return;
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      className={`w-full h-[50px] flex justify-center items-center relative rounded-sm ${
        valueFixed === null ? "cursor-normal" : "cursor-pointer"
      }`}
      aria-owns={open ? "mouse-over-popover" : undefined}
      aria-haspopup="true"
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      sx={{ backgroundColor: generateBackgroundColor(data.value) }}
      onClick={() => {
        if (valueFixed === null) return;
        onClickCell({ ...data, ...detectorData });
      }}
    >
      <Typography variant="detail" align="center" className="text-slate-900">
        {valueFixed}
      </Typography>
      <Popover
        role="tooltip"
        id="mouse-over-popover"
        sx={{
          pointerEvents: "none",
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box className="bg-white p-4 min-w-[280px] w-[280px]">
          <Box className="flex flex-col gap-1">
            <Typography variant="detail" fontWeight="bold" className="text-[#006DD9]" noWrap>
              {detectorData.label_prompt}
            </Typography>
            <Typography variant="detail" className="text-gray-600 flex items-center gap-1" noWrap>
              <span className="font-bold">ID:</span> {detectorData.detector_id}
            </Typography>
          </Box>

          <Divider className="my-1" />

          <Box className="flex flex-col gap-4">
            <Typography fontWeight="semibold" className="text-[#49494D]">
              Assesment Score
            </Typography>

            <Box className="flex justify-center items-center gap-2">
              <Typography
                fontWeight="semibold"
                className="text-black p-3 rounded-md"
                sx={{ backgroundColor: generateBackgroundColor(data.value) }}
              >
                {valueFixed}
              </Typography>

              <Box className="flex flex-col gap-2">
                <Typography variant="detail" fontWeight="semibold" className="text-gray-600 flex items-center gap-1">
                  Total assessments: <span className="font-medium text-[#006DD9]">{data.total}</span>
                </Typography>
                <Typography variant="detail" fontWeight="semibold" className="text-gray-600 flex items-center gap-1">
                  Correct assessments: <span className="font-medium text-[#006DD9]">{data.correct}</span>
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider className="mt-4 mb-2" />
        </Box>
      </Popover>
    </Box>
  );
}

interface AssesmentItemProps {
  cellData: Cell;
  xAxisLabelsLenght: number;
  onClickCell: (data: any) => void;
}

export const AssesmentItem = forwardRef((props: AssesmentItemProps, ref) => {
  const { cellData, onClickCell, xAxisLabelsLenght } = props;

  return (
    <Box ref={ref} className="cells col" sx={{ gridTemplateColumns: `250px repeat(${xAxisLabelsLenght}, 80px)` }}>
      <Box className="w-full h-full bg-white flex flex-col items-start justify-center px-2 rounded-sm">
        <Typography variant="detail" fontWeight="bold" className="text-slate-900 w-full" noWrap>
          {cellData.yId}
        </Typography>
        <Tooltip title={cellData.yLabel}>
          <Typography variant="detail" className="text-[#006DD9] w-full" noWrap>
            {cellData.yLabel}
          </Typography>
        </Tooltip>
      </Box>
      {cellData.yData.map((yData) => (
        <ItemWithTooltip
          key={`${cellData.yLabel}-${yData.xLabel}`}
          detectorData={{
            label_prompt: cellData.yLabel,
            detector_id: cellData.yId,
          }}
          data={yData}
          onClickCell={onClickCell}
        />
      ))}
    </Box>
  );
});
